import React, { useState, useEffect, useRef } from 'react';
import './Experience.css';

// Import the images
import iiitLogo from '../IIIT_Bangalore_Logo.svg.png';
import pharmeasyLogo from '../pharmeasy.png';
import catLogo from '../cat.png';
import redhatLogo from '../redhat.png';

interface ExperienceItem {
  year: string;
  role: string;
  company: string;
  description: string[]; // Change description to an array of strings
  technologies: string[];
  logo: string;
}

const EXPERIENCE: ExperienceItem[] = [
  {
    year: "JAN 2025 - CURRENT",
    role: "Software Engineer",
    company: "Red Hat (Promoted Apr 2026)",
    description: [
      "Shipped Ansible Automation Portal Installer to GA, enabling enterprise customers to deploy across AWS, VMware, and KVM with 50% faster onboarding through streamlined cloud-init workflows.",
      "Delivered hashicorp.terraform collection v1.2.0, unblocking Terraform Enterprise integrations for AAP customers and improving module reliability with 90%+ code coverage.",
      "Built AI-powered GitHub triage bot that reduced manual issue classification time by 70%, now actively used across Ansible engineering teams.",
      "Implemented HCP Terraform credential type in AWX, enabling secure Terraform Cloud/Enterprise integration for AAP 2.6 – a key customer-requested feature.",
      "Remediated 100+ CVE vulnerabilities, strengthening security posture and maintaining compliance for enterprise portal deployments.",
      "Contributed to open source projects: VS Code Ansible extension, Ansible Creator CLI, and ansible.platform collection – improving developer experience for the broader Ansible community."
    ],
    technologies: ["Python", "Ansible", "Terraform", "React", "TypeScript", "Go", "Kubernetes", "Podman", "OpenShift", "AWS", "Gemini AI", "GitHub Actions", "Backstage", "VS Code", "YAML", "Shell Script", "PostgreSQL"],
    logo: redhatLogo,
  },
  {
    year: "JUL 2024 - DEC 2024",
    role: "IT Application Developer and Analyst",
    company: "Caterpillar",
    description: [
      "Developed Python scripts for Power Platform automation.",
      "Built Fast APIs for enterprise integrations.",
      "Created Power Platform Component Framework solutions using React.",
      "Delivered Power Apps and Power Automate solutions for business workflows.",
    ],
    technologies: ["Python", "React", "Docker", "Cosmos DB", "Azure", "PowerApps", "PowerAutomate", "JavaScript", "Dataverse"],
    logo: catLogo,
  },
  {
    year: "DEC 2022 - JUN 2023",
    role: "Software Developer Intern",
    company: "PharmEasy",
    description: [
      "Worked on the Front End of the Surgicare website.",
      "Implemented features like Auto Coupon Application, Graded Coupons, and PSI enhancements, fostering a 13% increase in order completion, 30% surge in average order value, and 3X organic traffic.",
      "Worked on Docker build optimization.",
      "Worked on Arango DB and consistency of stored data.",
      "Developed Slack bots in Python to automate data consistency monitoring and alert relevant teams in case of discrepancies."
    ],
    technologies: ["Python", "Docker", "Kubernetes", "Arango DB", "React", "Next JS", "Express JS"],
    logo: pharmeasyLogo,
  },
  {
    year: "JUN 2022 - SEPT 2022",
    role: "Research Intern",
    company: "IIIT-Bangalore",
    description: [
      'Reviewed an existing research paper titled "Classification Trees for Imbalanced Data: Surface-To-Volume Regularization"',
      "Understood the mathematical concepts used behind SVR trees.",
      "Performed literary review for better understanding of existing results.",
      "Proposed a solution to extend SVR trees for Multiclass classification of balanced and imbalanced datasets."
    ],
    technologies: ["Python", "TensorFlow", "Keras", "SkLearn"],
    logo: iiitLogo,
  },
];

const Experience: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const experienceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = experienceRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  return (
    <section className="experience-section" ref={experienceRef}>
      <h2 className={isVisible ? 'slide-in' : 'slide-out'}>Experience</h2>
      <div className={`experience-list ${isVisible ? 'fade-in-list' : 'fade-out-list'}`}>
        {EXPERIENCE.map((exp, index) => (
          <div key={index} className={`experience-item ${isVisible ? 'fade-in-item' : 'fade-out-item'}`}>
            <div className="experience-year">
              <img src={exp.logo} alt={`${exp.company} Logo`} className="experience-logo" />
              <p className="experience-year-text">{exp.year}</p>
            </div>
            <div className="experience-details">
              <h2 className="experience-role">
                {exp.role} <br />
                <span className="experience-company">{exp.company}</span>
              </h2>
              <ul className="experience-description">
                {exp.description.map((desc, idx) => (
                  <li key={idx}>{desc}</li>
                ))}
              </ul>
              <div className="experience-technologies">
                {exp.technologies.map((tech, idx) => (
                  <span key={idx} className="technology-badge">{tech}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Experience;
